import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckinForm } from "../components/input/CheckinForm";
import { useBrands } from "../hooks/useBrand";
import { api } from "../lib/api";
import { Badge } from "../components/ui/Badge";
import type { ContentRun, DailyCheckin } from "../../../shared/types";

export function Dashboard() {
  const { data: brandsData, isLoading: brandsLoading } = useBrands();
  const brands = brandsData?.items?.map((item) => item.data) ?? [];
  const activeBrand = brands[0];
  const [lastCheckin, setLastCheckin] = useState<DailyCheckin | null>(null);

  const { data: runsData } = useQuery({
    queryKey: ["content-runs"],
    queryFn: () => api.get<{ data: ContentRun[] }>("/api/content/runs"),
    enabled: !!activeBrand,
  });

  if (brandsLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (!activeBrand) {
    return (
      <div className="py-12 text-center">
        <h2 className="mb-2 text-xl font-bold text-slate-900">Welcome to HelloConvo</h2>
        <p className="mb-6 text-sm text-slate-500">
          Set up your brand first to start creating content.
        </p>
        <a
          href="/brand"
          className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Set up brand
        </a>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Check-in section */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-lg font-semibold text-slate-900">
          What did you work on today?
        </h2>
        <CheckinForm
          brandId={activeBrand.id}
          onCheckinComplete={(checkin) => setLastCheckin(checkin)}
        />
        {lastCheckin && (
          <div className="mt-4 rounded-lg bg-green-50 p-3">
            <p className="text-sm text-green-700">
              Check-in saved! Your content pipeline will start processing.
            </p>
          </div>
        )}
      </section>

      {/* Recent content runs */}
      <section>
        <h2 className="mb-3 text-lg font-semibold text-slate-900">Recent content</h2>
        {runsData?.data?.length ? (
          <div className="space-y-3">
            {runsData.data.slice(0, 5).map((run) => (
              <a
                key={run.id}
                href={run.selected_idea_index !== null ? `/review/${run.id}` : `/ideas/${run.id}`}
                className="block rounded-lg border border-slate-200 bg-white p-4 hover:border-slate-300"
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-900">
                    {run.ideas?.[0]?.headline || "Content run"}
                  </p>
                  <Badge
                    variant={
                      run.agent_status?.repurpose === "ready"
                        ? "success"
                        : run.agent_status?.intake === "failed"
                          ? "error"
                          : "default"
                    }
                  >
                    {run.agent_status?.repurpose === "ready"
                      ? "Ready"
                      : run.agent_status?.intake === "failed"
                        ? "Failed"
                        : "Processing"}
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-slate-400">
                  {new Date(run.created_at).toLocaleDateString()}
                </p>
              </a>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400">
            No content runs yet. Submit a check-in to get started.
          </p>
        )}
      </section>
    </div>
  );
}
